import React, { useState, useEffect } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';
import ExportDropdown from '../components/common/ExportDropdown';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const PREDEFINED_TAGS = {
  'Speech Condition': ['dysarthric', 'fluent', 'accented', 'disordered', 'normal'],
  'Noise Condition': ['clean', 'café noise', 'telephone', 'street noise', 'clinical'],
  'Domain': ['clinical', 'customer service', 'interview', 'negotiation', 'language learning'],
  'Quality': ['validated', 'pilot', 'training', 'test'],
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getScoreColor(score) {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#0052CC';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}

function groupByMonthYear(items) {
  const groups = {};
  items.forEach(item => {
    const date = new Date(item.created_at);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    if (!groups[key]) groups[key] = { year: date.getFullYear(), month: date.getMonth(), items: [] };
    groups[key].items.push(item);
  });
  return Object.values(groups).sort((a, b) =>
    b.year !== a.year ? b.year - a.year : b.month - a.month
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '13px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px',
  },
  statCard: {
    backgroundColor: '#f9fafb', borderRadius: '10px', padding: '16px',
    border: '1px solid #e5e7eb', textAlign: 'center',
  },
  statValue: { fontSize: '28px', fontWeight: '800', color: '#0052CC' },
  statLabel: { fontSize: '12px', color: '#6b7280', marginTop: '4px', fontWeight: '500' },
  filterRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280' },
  select: {
    padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
    fontSize: '13px', backgroundColor: '#fff', outline: 'none', minWidth: '140px',
  },
  exportRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  exportBtn: {
    padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    backgroundColor: '#fff', color: '#374151',
  },
  exportBtnPrimary: {
    padding: '8px 16px', borderRadius: '8px', border: 'none',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    backgroundColor: '#0052CC', color: '#fff',
  },
  groupHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
    marginBottom: '6px', userSelect: 'none',
  },
  groupHeaderOpen: { backgroundColor: '#EBF5FF', border: '1px solid #bfdbfe' },
  groupHeaderClosed: { backgroundColor: '#fff', border: '1px solid #e5e7eb' },
  simCard: {
    backgroundColor: '#fff', borderRadius: '10px', padding: '16px',
    border: '1px solid #e5e7eb', marginBottom: '8px',
  },
  simHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  simTitle: { fontSize: '14px', fontWeight: '700', color: '#111827' },
  simMeta: { fontSize: '12px', color: '#6b7280', marginTop: '2px' },
  badge: {
    fontSize: '11px', fontWeight: '600', padding: '2px 8px',
    borderRadius: '20px', display: 'inline-block',
  },
  tagRow: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' },
  tag: {
    fontSize: '11px', fontWeight: '600', padding: '2px 8px',
    borderRadius: '20px', backgroundColor: '#dbeafe', color: '#0052CC',
    cursor: 'default',
  },
  tagRemove: {
    fontSize: '11px', fontWeight: '600', padding: '2px 8px',
    borderRadius: '20px', backgroundColor: '#dbeafe', color: '#0052CC',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
  },
  addTagBtn: {
    fontSize: '11px', fontWeight: '600', padding: '2px 8px',
    borderRadius: '20px', backgroundColor: '#f3f4f6', color: '#374151',
    cursor: 'pointer', border: '1px dashed #d1d5db',
  },
  turnCard: {
    padding: '10px 14px', borderRadius: '8px', marginBottom: '6px',
    fontSize: '13px', lineHeight: '1.6',
  },
  turnAI: { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' },
  turnUser: { backgroundColor: '#EBF5FF', border: '1px solid #bfdbfe' },
  perceived: {
    fontSize: '12px', color: '#92400e', backgroundColor: '#fef3c7',
    padding: '4px 8px', borderRadius: '6px', marginTop: '6px',
    borderLeft: '3px solid #d97706',
  },
  modal: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
  },
  modalCard: {
    backgroundColor: '#fff', borderRadius: '16px', padding: '28px',
    width: '100%', maxWidth: '480px', maxHeight: '80vh', overflowY: 'auto',
    boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
  },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '16px' },
  tagCategory: { fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '8px', marginTop: '16px' },
  tagGrid: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' },
  tagOption: {
    fontSize: '12px', fontWeight: '600', padding: '4px 10px',
    borderRadius: '20px', cursor: 'pointer', border: '1px solid #e5e7eb',
  },
  tagOptionSelected: { backgroundColor: '#0052CC', color: '#fff', border: '1px solid #0052CC' },
  tagOptionUnselected: { backgroundColor: '#f3f4f6', color: '#374151' },
  customTagInput: {
    width: '100%', padding: '8px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '13px', marginTop: '8px',
    boxSizing: 'border-box', outline: 'none',
  },
  modalActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' },
  saveTagBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '8px 20px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  cancelTagBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  loading: { textAlign: 'center', padding: '32px', color: '#6b7280' },
  distRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f3f4f6' },
  distLabel: { fontSize: '13px', color: '#374151', textTransform: 'capitalize' },
  distBar: { display: 'flex', alignItems: 'center', gap: '8px' },
  distBarFill: { height: '6px', borderRadius: '3px', backgroundColor: '#0052CC', minWidth: '4px' },
  distCount: { fontSize: '12px', fontWeight: '600', color: '#6b7280', minWidth: '24px', textAlign: 'right' },
};

export default function Corpus() {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState({});
  const [zipExporting, setZipExporting] = useState(false);
  const [zipError, setZipError] = useState('');
  const [expandedSim, setExpandedSim] = useState(null);
  const [turns, setTurns] = useState({});
  const [tagModal, setTagModal] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTag, setCustomTag] = useState('');
  const [savingTags, setSavingTags] = useState(false);
  const [selectedSims, setSelectedSims] = useState([]);

  // Filters
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [filterStt, setFilterStt] = useState('');

  //Mobile responsive
  const width = useWindowWidth();
  const isMobile = width < 768;

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [statsRes, simsRes] = await Promise.all([
        fetch(`${API}/api/corpus/stats?owner_id=${currentUser.uid}`),
        fetch(`${API}/api/corpus/simulations?owner_id=${currentUser.uid}`),
      ]);
      const statsData = await statsRes.json();
      const simsData = await simsRes.json();
      setStats(statsData);
      setSimulations(Array.isArray(simsData) ? simsData : []);

      // All groups closed by default
    } finally {
      setLoading(false);
    }
  }

  async function fetchFiltered() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ owner_id: currentUser.uid });
      if (filterLanguage) params.append('language', filterLanguage);
      if (filterMode) params.append('output_mode', filterMode);
      if (filterStt) params.append('stt_provider', filterStt);
      const res = await fetch(`${API}/api/corpus/simulations?${params}`);
      const data = await res.json();
      setSimulations(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }

  async function fetchTurns(simId) {
    if (turns[simId]) return;
    const res = await fetch(`${API}/api/corpus/simulations/${simId}/turns`);
    const data = await res.json();
    setTurns(prev => ({ ...prev, [simId]: data }));
  }

  function toggleGroup(key) {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleExpand(simId) {
    if (expandedSim === simId) {
      setExpandedSim(null);
    } else {
      setExpandedSim(simId);
      fetchTurns(simId);
    }
  }

  function openTagModal(sim) {
    setTagModal(sim);
    setSelectedTags(sim.tags || []);
    setCustomTag('');
  }

  function toggleTag(tag) {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  }

  async function saveTags() {
    if (!tagModal) return;
    setSavingTags(true);
    const allTags = [
      ...selectedTags,
      ...customTag.split(',').map(t => t.trim()).filter(Boolean),
    ];
    try {
      await fetch(`${API}/api/corpus/simulations/${tagModal.id}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags: [...new Set(allTags)] }),
      });
      setSimulations(prev => prev.map(s =>
        s.id === tagModal.id ? { ...s, tags: [...new Set(allTags)] } : s
      ));
      setTagModal(null);
    } finally {
      setSavingTags(false);
    }
  }

  function toggleSelectSim(simId) {
    setSelectedSims(prev =>
      prev.includes(simId) ? prev.filter(id => id !== simId) : [...prev, simId]
    );
  }

  async function handleExport(format) {
    if (selectedSims.length === 0) {
      setZipError('Please select at least one simulation to export.');
      return;
    }
    setZipError('');
    const params = new URLSearchParams({ owner_id: currentUser.uid, format });
    params.append('simulation_ids', selectedSims.join(','));
    window.open(`${API}/api/corpus/export?${params}`, '_blank');
  }

  async function handleAudioZipExport() {
    if (selectedSims.length === 0) {
      setZipError('Please select at least one simulation to export.');
      return;
    }
    setZipExporting(true);
    setZipError('');
    try {
      const params = new URLSearchParams({ owner_id: currentUser.uid });
      params.append('simulation_ids', selectedSims.join(','));

      const res = await fetch(`${API}/api/corpus/export-audio-zip?${params}`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Export failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `v2vlab_corpus_audio_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setZipError(err.message || 'Failed to export audio ZIP');
    } finally {
      setZipExporting(false);
    }
  }

  const groups = groupByMonthYear(simulations);
  const byYear = {};
  groups.forEach(g => {
    if (!byYear[g.year]) byYear[g.year] = [];
    byYear[g.year].push(g);
  });
  const sortedYears = Object.keys(byYear).sort((a, b) => b - a);

  const maxDist = (obj) => Math.max(...Object.values(obj || {}), 1);

  return (
    <AppLayout>
      <div style={{ ...styles.header, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '12px' : '0' }}>
        <div>
          <div style={styles.title}>Corpus</div>
          <div style={styles.sub}>Browse, tag, and export your generated speech corpus</div>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          gap: '6px',
        }}>
          <ExportDropdown
            disabled={selectedSims.length === 0}
            disabledHint="Select at least one simulation to export"
            loading={zipExporting}
            options={[
              { label: '🎙 Audio + Transcripts (ZIP)', sublabel: 'Full archival bundle — audio files plus transcripts', onClick: handleAudioZipExport },
              { label: '⬇ JSON', sublabel: 'Full corpus data', onClick: () => handleExport('json') },
              { label: '⬇ CSV', sublabel: 'Transcripts as spreadsheet', onClick: () => handleExport('csv') },
              { label: '⬇ Manifest', sublabel: 'Audio file index only', onClick: () => handleExport('manifest') },
            ]}
          />
          {selectedSims.length === 0 && !zipError && (
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
              Select simulations below to enable export.
            </div>
          )}
          {zipError && (
            <div style={{ fontSize: '12px', color: '#dc2626' }}>{zipError}</div>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Corpus Statistics</div>
          <div style={styles.statsGrid}>
            {[
              { label: 'Simulations', value: stats.total_simulations },
              { label: 'Total Turns', value: stats.total_turns },
              { label: 'Audio Turns', value: stats.total_audio_turns },
              { label: 'Perturbed Turns', value: stats.total_perturbed_turns },
              { label: 'Perceived Turns', value: stats.total_perceived_turns },
              { label: 'Avg STT Accuracy', value: stats.avg_stt_accuracy != null ? `${stats.avg_stt_accuracy}%` : '—' },
              { label: 'Avg Eval Score', value: stats.avg_evaluation_score != null ? `${stats.avg_evaluation_score}/100` : '—' },
            ].map(s => (
              <div key={s.label} style={styles.statCard}>
                <div style={styles.statValue}>{s.value ?? '—'}</div>
                <div style={styles.statLabel}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Distributions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginTop: '24px' }}>
            {[
              { title: 'Language', dist: stats.language_distribution },
              { title: 'Modality', dist: stats.modality_distribution },
              { title: 'LLM Provider', dist: stats.llm_distribution },
              { title: 'STT Provider', dist: stats.stt_distribution },
            ].map(({ title, dist }) => (
              <div key={title}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</div>
                {Object.entries(dist || {}).map(([key, count]) => (
                  <div key={key} style={styles.distRow}>
                    <div style={styles.distLabel}>{key}</div>
                    <div style={styles.distBar}>
                      <div style={{ ...styles.distBarFill, width: `${Math.round(count / maxDist(dist) * 60)}px` }} />
                      <div style={styles.distCount}>{count}</div>
                    </div>
                  </div>
                ))}
                {Object.keys(dist || {}).length === 0 && <div style={{ fontSize: '12px', color: '#9ca3af' }}>No data</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Filter Corpus</div>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <div style={styles.filterLabel}>Language</div>
            <select style={styles.select} value={filterLanguage} onChange={e => setFilterLanguage(e.target.value)}>
              <option value="">All Languages</option>
              {Object.keys(stats?.language_distribution || {}).map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <div style={styles.filterLabel}>Modality</div>
            <select style={styles.select} value={filterMode} onChange={e => setFilterMode(e.target.value)}>
              <option value="">All Modalities</option>
              <option value="text">Text</option>
              <option value="voice">Voice</option>
              <option value="voice_stt">Voice + STT</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <div style={styles.filterLabel}>STT Provider</div>
            <select style={styles.select} value={filterStt} onChange={e => setFilterStt(e.target.value)}>
              <option value="">All STT Providers</option>
              <option value="whisper">Whisper</option>
              <option value="deepgram">Deepgram</option>
              <option value="google">Google</option>
              <option value="assemblyai">AssemblyAI</option>
            </select>
          </div>
          <button style={styles.exportBtnPrimary} onClick={fetchFiltered}>Apply Filters</button>
          <button style={styles.exportBtn} onClick={() => { setFilterLanguage(''); setFilterMode(''); setFilterStt(''); fetchAll(); }}>Reset</button>
        </div>

        {selectedSims.length > 0 && (
          <div style={{ fontSize: '13px', color: '#0052CC', fontWeight: '600' }}>
            {selectedSims.length} simulation{selectedSims.length > 1 ? 's' : ''} selected for export
          </div>
        )}
      </div>

      {/* Corpus Browser */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>
          Corpus Browser ({simulations.length} simulations)
        </div>

        {loading ? (
          <div style={styles.loading}>Loading corpus...</div>
        ) : simulations.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#6b7280' }}>No completed simulations found.</div>
        ) : (
          sortedYears.map(year => (
            <div key={year}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px', marginTop: '16px' }}>
                {year}
              </div>
              {byYear[year].map(group => {
                const key = `${group.year}-${group.month}`;
                const isOpen = !!openGroups[key];
                return (
                  <div key={key}>
                    <div
                      style={{ ...styles.groupHeader, ...(isOpen ? styles.groupHeaderOpen : styles.groupHeaderClosed) }}
                      onClick={() => toggleGroup(key)}
                    >
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827', flex: 1 }}>
                        {MONTHS[group.month]}
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#0052CC', backgroundColor: '#dbeafe', padding: '2px 8px', borderRadius: '20px' }}>
                        {group.items.length} run{group.items.length !== 1 ? 's' : ''}
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>{isOpen ? '▲' : '▼'}</span>
                    </div>

                    {isOpen && group.items.map(sim => (
                      <div key={sim.id} style={styles.simCard}>
                        <div style={{ ...styles.simHeader, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : '0' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <input
                              type="checkbox"
                              checked={selectedSims.includes(sim.id)}
                              onChange={() => toggleSelectSim(sim.id)}
                              style={{ marginTop: '3px' }}
                            />
                            <div>
                              <div style={styles.simTitle}>{sim.scenario_title}</div>
                              <div style={styles.simMeta}>
                                {sim.llm_provider} · {sim.llm_model} · {sim.language || 'english'} · {sim.output_mode}
                                {sim.stt_provider && ` · STT: ${sim.stt_provider}`}
                              </div>
                              <div style={styles.simMeta}>
                                {new Date(sim.created_at).toLocaleDateString()} · {sim.current_turn} turns
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                              style={{ ...styles.exportBtn, fontSize: '12px', padding: '4px 10px' }}
                              onClick={() => openTagModal(sim)}
                            >
                              🏷 Tags
                            </button>
                            <button
                              style={{ ...styles.exportBtn, fontSize: '12px', padding: '4px 10px' }}
                              onClick={() => toggleExpand(sim.id)}
                            >
                              {expandedSim === sim.id ? 'Hide Turns' : 'View Turns'}
                            </button>
                          </div>
                        </div>

                        {/* Tags */}
                        {(sim.tags || []).length > 0 && (
                          <div style={styles.tagRow}>
                            {(sim.tags || []).map(tag => (
                              <span key={tag} style={styles.tag}>{tag}</span>
                            ))}
                          </div>
                        )}

                        {/* Turns */}
                        {expandedSim === sim.id && (
                          <div style={{ marginTop: '12px', borderTop: '1px solid #f3f4f6', paddingTop: '12px' }}>
                            {!turns[sim.id] ? (
                              <div style={{ fontSize: '12px', color: '#9ca3af' }}>Loading turns...</div>
                            ) : (
                              turns[sim.id].map(turn => (
                                <div key={turn.turn_number} style={{ ...styles.turnCard, ...(turn.agent_id === sim.agent_ids?.[0] ? styles.turnAI : styles.turnUser) }}>
                                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', marginBottom: '4px', letterSpacing: '0.5px' }}>
                                    Turn {turn.turn_number} · {turn.agent_name} {turn.agent_role ? `(${turn.agent_role})` : ''}
                                    {turn.perturbation_applied && <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#d97706', marginLeft: '6px' }}>perturbed</span>}
                                    {turn.audio_url && <span style={{ ...styles.badge, backgroundColor: '#dcfce7', color: '#16a34a', marginLeft: '6px' }}>audio</span>}
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>{turn.content}</div>
                                  {turn.perceived_content && turn.perturbation_applied && (
                                    <div style={styles.perceived}>
                                      🎙 Perceived: "{turn.perceived_content}"
                                    </div>
                                  )}
                                  {turn.audio_url && (
                                    <audio controls src={turn.audio_url} style={{ width: '100%', height: '28px', marginTop: '8px' }} />
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Tag Modal */}
      {tagModal && (
        <div style={styles.modal} onClick={e => e.target === e.currentTarget && setTagModal(null)}>
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>Tag: {tagModal.scenario_title}</div>

            {Object.entries(PREDEFINED_TAGS).map(([category, tags]) => (
              <div key={category}>
                <div style={styles.tagCategory}>{category}</div>
                <div style={styles.tagGrid}>
                  {tags.map(tag => (
                    <div
                      key={tag}
                      style={{
                        ...styles.tagOption,
                        ...(selectedTags.includes(tag) ? styles.tagOptionSelected : styles.tagOptionUnselected),
                      }}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div style={styles.tagCategory}>Custom Tags (comma-separated)</div>
            <input
              style={styles.customTagInput}
              placeholder="e.g. IRB-approved, Study-1, Grant-XYZ"
              value={customTag}
              onChange={e => setCustomTag(e.target.value)}
            />

            <div style={styles.modalActions}>
              <button style={styles.cancelTagBtn} onClick={() => setTagModal(null)}>Cancel</button>
              <button style={styles.saveTagBtn} onClick={saveTags} disabled={savingTags}>
                {savingTags ? 'Saving...' : 'Save Tags'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
